import {
  Banknote,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  Copy,
  Download,
  Eye,
  Info,
  RefreshCw,
  RotateCcw,
  SearchX,
  TrendingUp,
  Wifi,
  WifiOff,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "@/components/ui/Button";
import DataTable, { type Column } from "@/components/ui/DataTable";
import EmptyState from "@/components/ui/EmptyState";
import StatCard from "@/components/ui/StatCard";
import { TableSkeleton } from "@/components/ui/Skeleton";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import { useRetailerSession } from "@/components/feature/retailer/RetailerSessionContext";
import { installmentService } from "@/services/apiServices";
import { useInstallmentLiveUpdates, useRealtimeEvents } from "@/context/RealtimeEventsContext";
import type {
  FailureBreakdown,
  Installment,
  InstallmentCounts,
  InstallmentStats,
  InstallmentStatus,
} from "@/types/installment";
import {
  DEFAULT_RANGE,
  dayName,
  formatDateTime,
  formatDueDate,
  formatDueShort,
  formatINR,
  MONTHS,
  RANGES,
} from "./data";
import CollectionTrendChart, { type TrendPoint } from "./components/CollectionTrendChart";
import FailureReasons from "./components/FailureReasons";
import FilterBar from "./components/FilterBar";
import InstallmentDetailModal, { canRecordOffline } from "./components/InstallmentDetailModal";
import InstallmentStatusBadge from "./components/InstallmentStatusBadge";
import RecordOfflineModal, { type OfflineCollectionData } from "./components/RecordOfflineModal";
import RetryModal from "./components/RetryModal";
import SkipModal from "./components/SkipModal";
import StatusTabs, { TAB_DEFS } from "./components/StatusTabs";

const COLLECTED: InstallmentStatus[] = ["COLLECTED", "SETTLEMENT_PENDING", "SETTLED", "SETTLEMENT_FAILED"];

const EMPTY_COUNTS: InstallmentCounts = {
  all: 0, scheduled: 0, presented: 0, collected: 0, settled: 0, failed: 0, offline: 0, skipped: 0,
};

const EMPTY_STATS: InstallmentStats = {
  collectedAmount: 0, collectedCount: 0, scheduledAmount: 0, failedAmount: 0, failedCount: 0,
  overdueCount: 0, dueTodayCount: 0, totalCharges: 0, successRate: 0,
};

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function isoOf(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Turn a due-date range preset into concrete API bounds. */
function rangeToDates(range: string): { dateFrom?: string; dateTo?: string } {
  const now = new Date();
  switch (range) {
    case "Today":
      return { dateFrom: isoOf(now), dateTo: isoOf(now) };
    case "Tomorrow": {
      const t = new Date(now);
      t.setDate(t.getDate() + 1);
      return { dateFrom: isoOf(t), dateTo: isoOf(t) };
    }
    case "This Week": {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { dateFrom: isoOf(start), dateTo: isoOf(end) };
    }
    case "Last 7 Days": {
      const from = new Date(now);
      from.setDate(from.getDate() - 7);
      return { dateFrom: isoOf(from), dateTo: isoOf(now) };
    }
    case "Last 30 Days": {
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      return { dateFrom: isoOf(from), dateTo: isoOf(now) };
    }
    case "This Month": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { dateFrom: isoOf(start), dateTo: isoOf(end) };
    }
    case "Last Month": {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { dateFrom: isoOf(start), dateTo: isoOf(end) };
    }
    case "All Time":
    default:
      return {};
  }
}

function escapeCsv(value: unknown): string {
  const s = String(value ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function RetailerInstallments() {
  return (
    <ToastProvider>
      <InstallmentsInner />
    </ToastProvider>
  );
}

function InstallmentsInner() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const { retailerId, wallet, refreshWallet } = useRetailerSession();
  const { isConnected, connectionStatus } = useRealtimeEvents();

  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);

  const [headerRange, setHeaderRange] = useState(DEFAULT_RANGE);
  const [headerRangeOpen, setHeaderRangeOpen] = useState(false);

  const resolveInitialTab = useCallback(() => {
    const t = searchParams.get("tab");
    const s = searchParams.get("status");
    const query = t || s;
    if (query) {
      const match = TAB_DEFS.find(
        (d) =>
          d.key.toLowerCase() === query.toLowerCase() ||
          (d.apiStatus && d.apiStatus.toLowerCase() === query.toLowerCase())
      );
      if (match) return match.key;
    }
    return "All";
  }, [searchParams]);

  const [tab, setTab] = useState(resolveInitialTab);

  useEffect(() => {
    const nextTab = resolveInitialTab();
    if (nextTab !== tab) {
      setTab(nextTab);
      setPage(1);
    }
  }, [resolveInitialTab]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [mandate, setMandate] = useState(searchParams.get("ref") ?? "");
  const [bank, setBank] = useState("");
  const [filterRange, setFilterRange] = useState(DEFAULT_RANGE);
  const [filterRangeOpen, setFilterRangeOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [rows, setRows] = useState<Installment[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<InstallmentCounts>(EMPTY_COUNTS);
  const [overview, setOverview] = useState<Installment[]>([]);
  const [stats, setStats] = useState<InstallmentStats>(EMPTY_STATS);
  const [failures, setFailures] = useState<FailureBreakdown[]>([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState<Installment | null>(null);
  const [retryOpen, setRetryOpen] = useState(false);
  const [retryTarget, setRetryTarget] = useState<Installment | null>(null);
  const [retryLoading, setRetryLoading] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipTarget, setSkipTarget] = useState<Installment | null>(null);
  const [skipLoading, setSkipLoading] = useState(false);
  const [offlineOpen, setOfflineOpen] = useState(false);
  const [offlineTarget, setOfflineTarget] = useState<Installment | null>(null);
  const [offlineLoading, setOfflineLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [syncingRocketPay, setSyncingRocketPay] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, mandate, bank, filterRange, tab]);

  /** Overview across the header range - drives stat cards, trend and filter options. */
  const loadOverview = useCallback(async () => {
    try {
      const { dateFrom, dateTo } = rangeToDates(headerRange);
      const res = await installmentService.getInstallments({ retailerId, dateFrom, dateTo, limit: 2000 });
      setOverview(res?.data?.installments ?? []);
      setStats(res?.data?.stats ?? EMPTY_STATS);
      setFailures(res?.data?.failureBreakdown ?? []);
      if (res?.data?.counts) setCounts(res.data.counts);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load your installment summary");
    } finally {
      setLoading(false);
    }
  }, [retailerId, headerRange, toast]);

  const loadTable = useCallback(async () => {
    setTableLoading(true);
    try {
      const tabDef = TAB_DEFS.find((t) => t.key === tab);
      const { dateFrom, dateTo } = rangeToDates(filterRange);
      const res = await installmentService.getInstallments({
        retailerId,
        search,
        refNo: mandate || undefined,
        status: tabDef?.apiStatus,
        dateFrom,
        dateTo,
        page,
        limit: pageSize,
      });
      let list: Installment[] = res?.data?.installments ?? [];
      if (bank) list = list.filter((r) => r.bankName === bank);
      setRows(list);
      setTotal(res?.data?.pagination?.total ?? 0);
      setCounts(res?.data?.counts ?? EMPTY_COUNTS);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load installments");
      setRows([]);
    } finally {
      setTableLoading(false);
    }
  }, [retailerId, search, mandate, bank, tab, filterRange, page, pageSize, toast]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadTable();
  }, [loadTable]);

  // Debounce ref to prevent burst webhook events from spamming full re-fetches
  const debouncedReloadRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedReload = useCallback(() => {
    if (debouncedReloadRef.current) clearTimeout(debouncedReloadRef.current);
    debouncedReloadRef.current = setTimeout(() => {
      loadOverview();
      loadTable();
    }, 600);
  }, [loadOverview, loadTable]);

  // ─── [REAL-TIME SSE] Instant Installment Table Update ─────────────────────
  // Primary update path: When a recurring debit succeeds or fails on RocketPay,
  // RocketPay sends a Webhook to the backend, which immediately emits an SSE event.
  // This hook updates the matching installment row in-place without page reload.
  useInstallmentLiveUpdates((payload) => {
    const inst = payload?.installment;
    const status = payload?.status || inst?.status;
    const instId = payload?.installmentId || inst?.id;
    const mandateRefNo = payload?.mandateRefNo || payload?.refNo || inst?.mandateRefNo;

    if (inst && (instId || mandateRefNo)) {
      // Optimistic in-place update of the matching installment row
      setRows((prev) =>
        prev.map((r) =>
          r.id === instId ||
          r.installmentId === inst.installmentId ||
          (inst.rocketpayInstallmentId && r.rocketpayInstallmentId === inst.rocketpayInstallmentId)
            ? { ...r, ...inst }
            : r
        )
      );
      if (status) {
        toast.success(`Live update: Installment ${mandateRefNo || instId || ""} is now ${String(status).replace(/_/g, " ")}`);
      }
      // Debounced overview refresh (counts change)
      debouncedReload();
    } else {
      // Fallback: debounced full reload when payload has insufficient detail
      debouncedReload();
    }
  }, undefined, retailerId);

  // ─── [FALLBACK POLLING] Offline Auto-Sync (Every 60s) ──────────────────────
  // Activates ONLY when SSE is disconnected (`!isConnected`).
  // Guarantees data stays fresh even when Webhooks or SSE are unavailable.
  // Pauses automatically as soon as SSE reconnects.
  useEffect(() => {
    if (isConnected) return;
    const interval = setInterval(() => {
      loadOverview();
      loadTable();
    }, 60_000);
    return () => clearInterval(interval);
  }, [isConnected, loadOverview, loadTable]);

  const reloadAll = useCallback(async () => {
    await Promise.all([loadOverview(), loadTable(), refreshWallet()]);
  }, [loadOverview, loadTable, refreshWallet]);

  async function handleSyncRocketPay() {
    setSyncingRocketPay(true);
    try {
      const res = await installmentService.syncAll();
      toast.success(res?.message || "Installments synchronized successfully");
      await reloadAll();
    } catch (err: any) {
      toast.error(err?.message || "Could not sync installments");
      await reloadAll();
    } finally {
      setSyncingRocketPay(false);
    }
  }

  const mandateOptions = useMemo(
    () =>
      Array.from(new Map(overview.map((t) => [t.refNo, t])).values())
        .map((t) => ({ value: t.refNo, label: `${t.refNo} · ${t.customerName}` }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [overview]
  );

  const bankOptions = useMemo(
    () => Array.from(new Set(overview.map((t) => t.bankName).filter(Boolean))).map((b) => ({ value: b, label: b })),
    [overview]
  );

  const tabCounts = useMemo(() => {
    const map: Record<string, number> = {};
    TAB_DEFS.forEach((td) => {
      map[td.key] = counts[td.countKey] ?? 0;
    });
    return map;
  }, [counts]);

  const trendData: TrendPoint[] = useMemo(() => {
    const today = new Date();
    const out: TrendPoint[] = [];
    const pointsCount = headerRange === "This Week" || headerRange === "Last 7 Days" ? 7 : 30;
    for (let i = pointsCount - 1; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = isoOf(d);
      const dayItems = overview.filter(
        (t) => t.dueDate === key || t.collectionDate === key || t.offlineCollectedOn === key
      );
      out.push({
        label: formatDueShort(key),
        collected: dayItems.filter((t) => COLLECTED.includes(t.status) || t.status === "COLLECTED_OFFLINE").length,
        failed: dayItems.filter((t) => t.status === "FAILED").length,
      });
    }
    return out;
  }, [overview, headerRange]);

  const offlineList = overview.filter((t) => t.status === "COLLECTED_OFFLINE");
  const settledList = overview.filter((t) => t.status === "SETTLED");
  const presentedTotal = stats.outcomeCount ?? (stats.collectedCount + stats.failedCount + (counts.offline || offlineList.length));
  const charge = wallet?.installmentRate ?? 40;

  function resetFilters() {
    setSearch("");
    setMandate("");
    setBank("");
    setFilterRange(DEFAULT_RANGE);
    setTab("All");
  }

  async function scheduleRetry(retryDate: string) {
    if (!retryTarget) return;
    setRetryLoading(true);
    try {
      // Convert display date ("25 Jan 2026") to ISO format ("2026-01-25") for RocketPay
      let schedule_date = retryDate;
      if (retryDate && retryDate.includes(" ")) {
        const parts = retryDate.split(" ");
        if (parts.length === 3) {
          const monthIdx = MONTHS.indexOf(parts[1]) + 1;
          if (monthIdx > 0) {
            schedule_date = `${parts[2]}-${String(monthIdx).padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
          }
        }
      }
      const res = await installmentService.retry(retryTarget.id, schedule_date, retryDate);
      toast.success(res?.message || "Retry scheduled");
      setRetryOpen(false);
      setRetryTarget(null);
      await reloadAll();
    } catch (err: any) {
      toast.error(err?.message || "Could not schedule the retry");
    } finally {
      setRetryLoading(false);
    }
  }

  async function confirmSkip(reason: string, remarks: string) {
    if (!skipTarget) return;
    setSkipLoading(true);
    try {
      const res = await installmentService.skip(skipTarget.id, remarks ? `${reason} — ${remarks}` : reason);
      toast.success(res?.message || "Installment skipped");
      setSkipOpen(false);
      setSkipTarget(null);
      await reloadAll();
    } catch (err: any) {
      toast.error(err?.message || "Could not skip this installment");
    } finally {
      setSkipLoading(false);
    }
  }

  async function recordOffline(data: OfflineCollectionData) {
    if (!offlineTarget) return;
    setOfflineLoading(true);
    try {
      const parts = data.collectionDate.split(" ");
      const collectionDate =
        parts.length === 3
          ? `${parts[2]}-${pad2(MONTHS.indexOf(parts[1]) + 1)}-${parts[0].padStart(2, "0")}`
          : data.collectionDate;

      const res = await installmentService.markOffline(offlineTarget.id, {
        amount: data.amountReceived,
        paymentMode: data.mode,
        reference: data.reference,
        collectionDate,
        remark: data.remarks,
        receiptFileName: data.receipt ?? undefined,
      });
      toast.success(res?.message || "Offline collection recorded");
      setOfflineOpen(false);
      setOfflineTarget(null);
      await reloadAll();
    } catch (err: any) {
      toast.error(err?.message || "Could not record the offline collection");
    } finally {
      setOfflineLoading(false);
    }
  }

  async function refreshRow(row: Installment) {
    try {
      // Try the RocketPay-first refresh endpoint
      let fresh: Installment | undefined;
      try {
        const rpRes = await installmentService.refresh(row.id);
        fresh = rpRes?.data?.installment;
      } catch (_rpErr) {
        // Fallback to local getById
        const res = await installmentService.getById(row.id);
        fresh = res?.data?.installment;
      }
      if (fresh) {
        setRows((prev) => prev.map((r) => (r.id === fresh!.id ? fresh! : r)));
        setDetailTarget((prev) => (prev && prev.id === fresh!.id ? fresh! : prev));
        toast.info(`Status updated: ${fresh.status.replace(/_/g, " ")}`);
      }
    } catch (err: any) {
      toast.error(err?.message || "Could not refresh this installment");
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      const tabDef = TAB_DEFS.find((t) => t.key === tab);
      const { dateFrom, dateTo } = rangeToDates(filterRange);
      const res = await installmentService.getInstallments({
        retailerId,
        search,
        refNo: mandate || undefined,
        status: tabDef?.apiStatus,
        dateFrom,
        dateTo,
        limit: 10000,
      });
      const list: Installment[] = res?.data?.installments ?? [];
      if (list.length === 0) {
        toast.error("No installments to export");
        return;
      }
      const headers = [
        "Installment ID", "Mandate", "Customer", "Installment", "Amount",
        "Due Date", "Status", "UTR", "Platform Charge",
      ];
      const body = list.map((r) => [
        r.installmentId,
        r.refNo,
        r.customerName,
        `${r.installmentNumber} of ${r.totalInstallments}`,
        r.amount,
        r.dueDate,
        r.status,
        r.utrNumber ?? "",
        r.platformCharge,
      ]);
      const csv = [headers, ...body].map((row) => row.map(escapeCsv).join(",")).join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `installments-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${list.length} installment${list.length === 1 ? "" : "s"}`);
    } catch (err: any) {
      toast.error(err?.message || "Could not export installments");
    } finally {
      setExporting(false);
    }
  }

  function openDetail(row: Installment) {
    setDetailTarget(row);
    setDetailOpen(true);
  }

  function goToMandate(refNo: string) {
    navigate(`/retailer/mandates/${refNo}`);
  }

  function canSkip(row: Installment): boolean {
    if (row.status !== "SCHEDULED") return false;
    const due = new Date(`${row.dueDate}T00:00:00`);
    const cutoff = new Date();
    cutoff.setHours(0, 0, 0, 0);
    cutoff.setDate(cutoff.getDate() + 2);
    return due.getTime() > cutoff.getTime();
  }

  const columns: Column<Installment>[] = [
    {
      key: "installmentId",
      header: "Installment ID",
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-[13px] font-medium text-ink">{row.installmentId}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard
                .writeText(row.installmentId)
                .then(() => toast.success("Installment ID copied"))
                .catch(() => toast.error("Could not copy"));
            }}
            className="text-ink-muted hover:text-primary-600 p-0.5 cursor-pointer rounded hover:bg-slate-100"
            title="Copy Installment ID"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
    {
      key: "rocketpayInstallmentId",
      header: "Gateway ID",
      render: (row) => {
        const rpId = row.rocketpayInstallmentId;
        if (!rpId) {
          return <span className="text-xs text-[#94A3B8]">—</span>;
        }
        return (
          <div className="flex items-center gap-1.5">
            <span
              className="inline-flex items-center gap-1 font-mono text-xs text-primary-700 bg-primary-50 border border-primary-200 px-2 py-0.5 rounded whitespace-nowrap"
              title={rpId}
            >
              <Zap className="h-3 w-3 text-primary-500 shrink-0" />
              {rpId.slice(0, 8)}…{rpId.slice(-4)}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard
                  .writeText(rpId)
                  .then(() => toast.success("Gateway ID copied"))
                  .catch(() => toast.error("Could not copy"));
              }}
              className="text-ink-muted hover:text-primary-600 p-0.5 cursor-pointer rounded hover:bg-slate-100"
              title={`Copy full Gateway ID: ${rpId}`}
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      },
    },
    {
      key: "refNo",
      header: "Mandate",
      render: (row) => (
        <div>
          <button
            type="button"
            onClick={() => goToMandate(row.refNo)}
            className="text-[13px] font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
          >
            {row.refNo}
          </button>
          <p className="text-xs text-ink-secondary">{row.customerName}</p>
        </div>
      ),
    },
    {
      key: "installmentNumber",
      header: "Installment",
      render: (row) => (
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-ink">
              {row.installmentNumber} of {row.totalInstallments}
            </p>
            {row.retryCount > 0 && (
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                <RotateCcw className="h-3 w-3" />
                Retry {row.retryCount}
              </span>
            )}
          </div>
          <p className="text-xs text-ink-secondary">{row.frequency}</p>
        </div>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (row) => <span className="whitespace-nowrap text-sm font-medium text-ink">{formatINR(row.amount)}</span>,
    },
    {
      key: "dueDate",
      header: "Due Date",
      render: (row) => (
        <div>
          <p className="text-[13px] text-ink">{formatDueDate(row.dueDate)}</p>
          <p className="text-xs text-ink-secondary">{dayName(row.dueDate)}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => <InstallmentStatusBadge status={row.status} />,
    },
    {
      key: "collection",
      header: "Collection",
      render: (row) => {
        if (row.status === "COLLECTED_OFFLINE")
          return (
            <div className="flex items-start gap-2">
              <Banknote className="mt-0.5 h-4 w-4 shrink-0 text-[#1E40AF]" />
              <div>
                <p className="text-[13px] text-ink">
                  {row.offlineCollectedOn ? formatDateTime(row.offlineCollectedOn) : "—"}
                </p>
                <p className="text-xs text-ink-secondary">
                  {row.offlineMode}
                  {row.offlineReference ? ` · ${row.offlineReference}` : ""}
                </p>
              </div>
            </div>
          );
        if (COLLECTED.includes(row.status))
          return (
            <div className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" strokeWidth={2.5} />
              <div>
                <p className="text-[13px] text-ink">{row.presentedAt ? formatDateTime(row.presentedAt) : "—"}</p>
                <p className="font-mono text-xs text-ink-secondary">{row.utrNumber ?? "—"}</p>
              </div>
            </div>
          );
        if (row.status === "FAILED")
          return (
            <div className="flex items-start gap-2">
              <X className="mt-0.5 h-4 w-4 shrink-0 text-danger" strokeWidth={2.5} />
              <div>
                <p className="text-xs text-danger">{row.failureReason}</p>
                <p className="text-[11px] text-[#94A3B8]">{row.failureCode}</p>
              </div>
            </div>
          );
        return <span className="text-[#94A3B8]">—</span>;
      },
    },
    {
      key: "settlement",
      header: "Settlement",
      render: (row) => {
        if (row.status === "SETTLED")
          return (
            <div className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" strokeWidth={2.5} />
              <div>
                <p className="text-[13px] text-ink">{row.settledAt ? formatDateTime(row.settledAt) : "—"}</p>
                <p className="text-xs text-ink-secondary">{formatINR(row.settlementAmount)}</p>
              </div>
            </div>
          );
        if (row.status === "SETTLEMENT_PENDING")
          return (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 shrink-0 text-warning" />
              <span className="text-[13px] text-ink">In progress</span>
            </div>
          );
        if (row.status === "SETTLEMENT_FAILED")
          return (
            <div className="flex items-center gap-2">
              <X className="h-4 w-4 shrink-0 text-danger" strokeWidth={2.5} />
              <span className="text-[13px] text-danger">Failed</span>
            </div>
          );
        return <span className="text-[#94A3B8]">—</span>;
      },
    },
    {
      key: "charges",
      header: "Charges",
      align: "right",
      render: (row) => {
        if (row.status === "COLLECTED_OFFLINE")
          return (
            <span className="text-xs text-[#64748B]" title="No charge applies to offline collections">
              —
            </span>
          );
        return row.platformCharge > 0 ? (
          <div className="text-right">
            <p className="whitespace-nowrap text-[13px] text-ink">{formatINR(row.platformCharge)}</p>
          </div>
        ) : (
          <span className="text-[#94A3B8]">—</span>
        );
      },
    },
  ];

  function rowActions(row: Installment) {
    const retryable = row.status === "FAILED";
    const skippable = canSkip(row);
    const recordable = canRecordOffline(row);
    return (
      <>
        <button
          type="button"
          onClick={() => openDetail(row)}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink hover:bg-slate-50 cursor-pointer"
        >
          <Eye className="h-4 w-4 text-ink-muted" />
          View Details
        </button>
        <button
          type="button"
          disabled={!retryable}
          title={retryable ? undefined : "Only failed installments can be retried"}
          onClick={() => {
            setRetryTarget(row);
            setRetryOpen(true);
          }}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
        >
          <RefreshCw className="h-4 w-4 text-ink-muted" />
          Retry Collection
        </button>
        <button
          type="button"
          disabled={!recordable}
          title={
            recordable
              ? undefined
              : "Only failed or upcoming installments can be recorded as collected offline."
          }
          onClick={() => {
            setOfflineTarget(row);
            setOfflineOpen(true);
          }}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
        >
          <Banknote className="h-4 w-4 text-ink-muted" />
          Mark as Collected Offline
        </button>
        <button
          type="button"
          disabled={!skippable}
          title={
            skippable
              ? undefined
              : "Installments can only be skipped at least 2 days before the due date."
          }
          onClick={() => {
            setSkipTarget(row);
            setSkipOpen(true);
          }}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
        >
          <RotateCcw className="h-4 w-4 text-ink-muted" />
          Skip Installment
        </button>
        <button
          type="button"
          onClick={() => refreshRow(row)}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink hover:bg-slate-50 cursor-pointer"
        >
          <RefreshCw className="h-4 w-4 text-ink-muted" />
          Refresh Status
        </button>
      </>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-ink">Installments</h1>
            <p className="mt-1 text-sm text-ink-secondary">
              Track scheduled debits and collection status across your mandates
            </p>
          </div>
          {/* Real-time connection status indicator */}
          <div
            title={connectionStatus === "connected" ? "Live updates active" : connectionStatus === "connecting" ? "Connecting..." : "Offline — polling every 60s"}
            className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors self-start mt-1 ${
              connectionStatus === "connected"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : connectionStatus === "connecting"
                ? "border-amber-200 bg-amber-50 text-amber-700"
                : "border-slate-200 bg-slate-50 text-slate-500"
            }`}
          >
            {connectionStatus === "connected" ? (
              <Wifi className="h-3 w-3" />
            ) : (
              <WifiOff className="h-3 w-3" />
            )}
            {connectionStatus === "connected" ? "Live" : connectionStatus === "connecting" ? "Connecting" : "Offline"}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <button
              type="button"
              onClick={() => setHeaderRangeOpen((v) => !v)}
              className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-slate-300 bg-white px-3.5 text-sm font-medium text-ink hover:bg-slate-50 cursor-pointer"
            >
              <Calendar className="h-4 w-4 text-ink-muted" />
              {headerRange}
              <ChevronDown className="h-4 w-4 text-ink-muted" />
            </button>
            {headerRangeOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setHeaderRangeOpen(false)} />
                <div className="absolute right-0 z-20 mt-2 w-44 rounded-lg border border-line bg-white py-1 shadow-dropdown animate-fadeIn">
                  {RANGES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => {
                        setHeaderRange(r);
                        setHeaderRangeOpen(false);
                      }}
                      className={`block w-full whitespace-nowrap px-4 py-2 text-left text-sm cursor-pointer ${
                        headerRange === r ? "bg-primary-50 text-primary-600" : "text-ink hover:bg-slate-50"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <Button
            variant="secondary"
            onClick={handleSyncRocketPay}
            loading={syncingRocketPay}
            leftIcon={<RefreshCw className={`h-4 w-4 ${syncingRocketPay ? "animate-spin" : ""}`} />}
            title="Refresh and sync all installment statuses with payment gateway"
          >
            Sync Schedule
          </Button>
          <Button
            variant="secondary"
            onClick={handleExport}
            loading={exporting}
            leftIcon={<Download className="h-4 w-4" />}
          >
            Export
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 flex flex-col gap-5">
          <TableSkeleton rows={4} columns={4} />
          <TableSkeleton rows={5} columns={9} />
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-4">
          {/* Summary cards */}
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 sm:gap-4">
            <StatCard
              label="Scheduled"
              value={formatINR(stats.scheduledAmount)}
              icon={Calendar}
              iconBg="bg-primary-50"
              iconColor="text-primary-600"
              trendLabel={`${counts.scheduled} upcoming`}
            />
            <StatCard
              label="Collected"
              value={formatINR(stats.collectedAmount)}
              icon={CheckCircle2}
              iconBg="bg-success-bg"
              iconColor="text-success"
              trendLabel={`${stats.collectedCount} collected`}
            />
            <StatCard
              label="Failed"
              value={formatINR(stats.failedAmount)}
              icon={XCircle}
              iconBg="bg-danger-bg"
              iconColor="text-danger"
              trendLabel={`${stats.failedCount} installments`}
            />
            <StatCard
              label="Settled"
              value={formatINR(stats.settledAmount ?? settledList.reduce((s, t) => s + (t.settlementAmount || t.amount || 0), 0))}
              icon={Banknote}
              iconBg="bg-[#D1FAE5]"
              iconColor="text-[#047857]"
              trendLabel={`${counts.settled} settled`}
            />
            <StatCard
              label="Collected Offline"
              value={formatINR(stats.offlineAmount ?? offlineList.reduce((s, t) => s + t.amount, 0))}
              icon={Banknote}
              iconBg="bg-[#EFF6FF]"
              iconColor="text-[#1E40AF]"
              trendLabel={`${counts.offline} offline`}
            />
            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-1">
                  <span className="text-[13px] text-ink-secondary">Collection Rate</span>
                  <span className="text-[30px] font-bold leading-tight text-ink">{stats.successRate}%</span>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-info-bg text-info">
                  <TrendingUp className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3 text-xs text-ink-muted">of {presentedTotal} with an outcome</div>
              <p className="mt-1 text-xs text-ink-secondary">Includes offline collections</p>
            </div>
          </div>

          {/* Charges strip */}
          <div className="flex flex-col items-start justify-between gap-3 rounded-[10px] border border-[#BAE6FD] bg-info-bg p-[14px] sm:flex-row sm:items-center">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-5 w-5 items-center justify-center text-info">
                <Info className="h-5 w-5" />
              </span>
              <p className="text-[13px] text-ink">
                {formatINR(charge)} platform charge is deducted from your wallet for each successfully collected
                installment. Failed and offline collections are not charged.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/retailer/wallet")}
              className="whitespace-nowrap text-[13px] font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
            >
              View wallet ledger
            </button>
          </div>

          {/* Two-column section */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[62%_38%]">
            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-ink">Collection Trend</h3>
                <div className="flex items-center gap-4 text-xs text-ink-secondary">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-success" />
                    Collected
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-danger" />
                    Failed
                  </span>
                </div>
              </div>
              <div className="mt-5">
                <CollectionTrendChart data={trendData} />
              </div>
            </div>
            <FailureReasons stats={failures} />
          </div>

          {/* Tabs */}
          <StatusTabs counts={tabCounts} active={tab} onChange={setTab} />

          {/* Filter bar */}
          <FilterBar
            search={searchInput}
            onSearch={setSearchInput}
            mandate={mandate}
            onMandate={setMandate}
            bank={bank}
            onBank={setBank}
            range={filterRange}
            onRange={setFilterRange}
            rangeOpen={filterRangeOpen}
            onRangeOpen={setFilterRangeOpen}
            onReset={resetFilters}
            mandateOptions={mandateOptions}
            bankOptions={bankOptions}
          />

          {tableLoading ? (
            <TableSkeleton rows={5} columns={9} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={SearchX}
              title="No installments found"
              description={
                counts.all === 0
                  ? "Installment schedules appear here once a customer authorises one of your mandates."
                  : "Try adjusting your filters or selecting a different tab."
              }
              actionLabel="Reset Filters"
              onAction={resetFilters}
            />
          ) : (
            <DataTable
              columns={columns}
              data={rows}
              rowKey={(r) => r.id}
              actions={rowActions}
              pageSizeOptions={[25, 50, 100]}
              onRowClick={openDetail}
              serverPagination={{
                page,
                pageSize,
                total,
                onPageChange: setPage,
                onPageSizeChange: (n) => {
                  setPageSize(n);
                  setPage(1);
                },
              }}
            />
          )}
        </div>
      )}

      <InstallmentDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        installment={detailTarget}
        mandate={null}
        onCopyUtr={(utr) => {
          navigator.clipboard
            .writeText(utr)
            .then(() => toast.success("UTR copied to clipboard"))
            .catch(() => toast.error("Could not copy UTR"));
        }}
        onRetry={(i) => {
          setDetailOpen(false);
          setRetryTarget(i);
          setRetryOpen(true);
        }}
        onSkip={(i) => {
          setDetailOpen(false);
          setSkipTarget(i);
          setSkipOpen(true);
        }}
        onRecordOffline={(i) => {
          setDetailOpen(false);
          setOfflineTarget(i);
          setOfflineOpen(true);
        }}
      />

      <RetryModal
        open={retryOpen}
        onClose={() => setRetryOpen(false)}
        installment={retryTarget}
        onConfirm={scheduleRetry}
        loading={retryLoading}
      />

      <SkipModal
        open={skipOpen}
        onClose={() => setSkipOpen(false)}
        installment={skipTarget}
        onConfirm={confirmSkip}
        loading={skipLoading}
      />

      <RecordOfflineModal
        open={offlineOpen}
        onClose={() => setOfflineOpen(false)}
        installment={offlineTarget}
        onConfirm={recordOffline}
        loading={offlineLoading}
      />
    </div>
  );
}